const VALID_INTENTS = ['IMPORT_CLASSES', 'SYNC_CLASS_CHANGE', 'TEACHER_LOGIN'] as const;

export type ThirdPartyAccountLinkContext = {
	intent: (typeof VALID_INTENTS)[number];
	extra: Record<string, unknown>;
};

type StateBoundContext = {
	state: string;
	context: ThirdPartyAccountLinkContext;
};

export const THIRD_PARTY_ACCOUNT_LINK_CONTEXT_STORAGE_KEY = 'thirdPartyAccountLinkContext';

const parseStateParam = (url: string): string => {
	try {
		return new URL(url).searchParams.get('state') ?? '';
	} catch {
		return '';
	}
};

const isStateBoundContext = (value: unknown): value is StateBoundContext => {
	const candidate = value as { state?: unknown; context?: { intent?: unknown; extra?: unknown } } | null;
	return (
		typeof candidate?.state === 'string' &&
		VALID_INTENTS.includes(candidate.context?.intent as ThirdPartyAccountLinkContext['intent']) &&
		typeof candidate.context?.extra === 'object' &&
		candidate.context.extra !== null
	);
};

const storeBoundContext = (initiationUrl: string, context: ThirdPartyAccountLinkContext): void => {
	localStorage.removeItem(THIRD_PARTY_ACCOUNT_LINK_CONTEXT_STORAGE_KEY);
	const stateBoundContext: StateBoundContext = {
		state: parseStateParam(initiationUrl),
		context: context,
	};
	sessionStorage.setItem(THIRD_PARTY_ACCOUNT_LINK_CONTEXT_STORAGE_KEY, JSON.stringify(stateBoundContext));
};

export const redirectToThirdPartyOAuth = (initiationUrl: string, context: ThirdPartyAccountLinkContext): void => {
	storeBoundContext(initiationUrl, context);
	window.location.replace(initiationUrl);
};

export const clearThirdPartyAccountLinkContext = (): void => {
	sessionStorage.removeItem(THIRD_PARTY_ACCOUNT_LINK_CONTEXT_STORAGE_KEY);
	localStorage.removeItem(THIRD_PARTY_ACCOUNT_LINK_CONTEXT_STORAGE_KEY);
};

export const getThirdPartyAccountLinkContext = (callbackUrl: string): ThirdPartyAccountLinkContext | null => {
	const raw = sessionStorage.getItem(THIRD_PARTY_ACCOUNT_LINK_CONTEXT_STORAGE_KEY);
	if (raw === null) {
		return null;
	}
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		clearThirdPartyAccountLinkContext();
		return null;
	}
	if (!isStateBoundContext(parsed)) {
		clearThirdPartyAccountLinkContext();
		return null;
	}
	const callbackState = parseStateParam(callbackUrl);
	if (callbackState === '' || parsed.state !== callbackState) {
		return null;
	}
	return parsed.context;
};

export const consumeThirdPartyAccountLinkContext = (callbackUrl: string): ThirdPartyAccountLinkContext | null => {
	const storedContext = getThirdPartyAccountLinkContext(callbackUrl);
	clearThirdPartyAccountLinkContext();
	return storedContext;
};
